import type { ReactNode, RefObject } from 'react'
import { Link } from 'react-router'
import { AlertTriangle, Check, Circle, Minus } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import type { PropertyCalendarReview } from '@/api/client'
import {
  chaseStatusLabel,
  formatInstantDays,
  reviewRowHref,
  reviewWindowState,
  type ReviewWindowState,
} from '@/lib/review-events'
import { formatSupplyDay } from '@/lib/supply-events'
import { cn } from '@/lib/utils'
import { SectionLoading } from './section-loading'
import { useRangeWords } from './range-words'

/**
 * What the calendar read says about this stay's review window. `pending` is
 * "not read yet", shown as a loading block; an `error` layer is said out loud, because
 * an unreadable layer is not "no review"; `ok` with no review is a stay the
 * layer does not list (a direct booking, or a Vrbo stay no chase has reached),
 * which is said as such. `today` is the day of the read's own `generated_at`
 * in the property's zone, for whether the window is still open.
 */
export type StayReview =
  | { status: 'pending' }
  | { status: 'error'; message: string }
  | { status: 'ok'; review: PropertyCalendarReview | null; today: string | null }

/** What `?section=` asks the stay sheet to scroll to for the review section. */
export const REVIEW_SECTION = 'reviews'

const WINDOW_STATE_LABEL: Record<ReviewWindowState, string> = {
  upcoming: 'Opens after checkout',
  open: 'Open',
  closed: 'Closed',
}

const HOST_SOURCE_NOTE: Record<string, string> = {
  inferred: 'inferred from when Airbnb revealed their review',
  manual: 'marked by you',
}

function Tick({ state }: { state: 'done' | 'open' | 'unknown' }) {
  const Icon = state === 'done' ? Check : state === 'open' ? Circle : Minus
  return (
    <span
      aria-hidden="true"
      data-testid="review-tick"
      data-state={state}
      className={cn(
        'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
        state === 'done'
          ? 'border-mint bg-mint/20 text-mint'
          : 'border-border bg-transparent text-textFaint'
      )}
    >
      <Icon className="h-2.5 w-2.5" strokeWidth={state === 'done' ? 3 : 2} />
    </span>
  )
}

function Row({
  label,
  testId,
  tick,
  children,
}: {
  label: string
  testId: string
  tick?: 'done' | 'open' | 'unknown'
  children: ReactNode
}) {
  return (
    <div className="flex items-start gap-2.5" data-testid={testId}>
      {tick ? <Tick state={tick} /> : <span aria-hidden="true" className="w-4 shrink-0" />}
      <div className="min-w-0 flex-1">
        <dt className="text-xs text-textMuted">{label}</dt>
        <dd className="text-sm">{children}</dd>
      </div>
    </div>
  )
}

const day = (value: string) => formatSupplyDay(value, { withYear: true })

function windowRow(review: PropertyCalendarReview, today: string | null): ReactNode {
  const closes = review.window_closes_on
  if (!closes) {
    return (
      <Row label="Window closes" testId="review-window">
        No review deadline on this channel
      </Row>
    )
  }
  const state = reviewWindowState(review, today)
  return (
    <Row label="Window closes" testId="review-window">
      <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <span className="tabular-nums">{day(closes)}</span>
        {state && (
          <Badge variant="secondary" className="text-[10px]" data-testid="review-window-state">
            {WINDOW_STATE_LABEL[state]}
          </Badge>
        )}
      </span>
    </Row>
  )
}

/**
 * The stay's review window: when it closes, whether each side has reviewed,
 * and what the chase has done. Read-only. The one link goes to this stay's row on
 * the Reviews page, where the review and thank-you drafts live and a person reads
 * and sends them; nothing here sends, reminds or records anything.
 *
 * Two nulls are different things. A guest review not seen is "not seen yet"
 * (Airbnb hides it until both have reviewed or the window ends), and it does
 * not depend on the chase. Without a chase record the host's review, the
 * reminders and the thank-you are not known at all, and are said to be "not
 * tracked", never "no".
 */
export function ReviewSection({
  review: state,
  timeZone,
  sectionRef,
}: {
  review: StayReview
  /** The property's zone: the clock the reminder and thank-you days are on. */
  timeZone: string
  /** Where the stay sheet scrolls to when a review block on the grid opened it. */
  sectionRef?: RefObject<HTMLElement | null>
}) {
  const words = useRangeWords()
  if (state.status === 'pending') return <SectionLoading label="Reviews" />

  let content: ReactNode
  if (state.status === 'error') {
    content = (
      <p className="flex items-center gap-1.5 text-sm text-danger" role="status">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {state.message}
      </p>
    )
  } else if (!state.review) {
    content = (
      <p className="text-sm text-textMuted" data-testid="review-none">
        No review window for this stay in {words.shown}.
      </p>
    )
  } else {
    content = <ReviewBody review={state.review} today={state.today} timeZone={timeZone} />
  }

  return (
    <section
      ref={sectionRef}
      aria-label="Reviews"
      className="scroll-mt-0 space-y-2"
      data-testid="stay-sheet-reviews"
    >
      <h3 className="text-xs font-semibold uppercase tracking-wider text-textFaint">Reviews</h3>
      {content}
    </section>
  )
}

function ReviewBody({
  review,
  today,
  timeZone,
}: {
  review: PropertyCalendarReview
  today: string | null
  timeZone: string
}) {
  const tracked = review.has_chase_record
  const airbnb = review.channel?.toLowerCase() === 'airbnb'
  const hostNote = review.host_review_source ? HOST_SOURCE_NOTE[review.host_review_source] : null
  const skipped = tracked && review.status === 'skipped'

  return (
    <div className="space-y-3">
      <dl className="space-y-2.5">
        {windowRow(review, today)}

        <Row
          label="Guest reviewed"
          testId="review-guest"
          tick={review.guest_reviewed_on ? 'done' : 'open'}
        >
          {review.guest_reviewed_on ? (
            <span className="tabular-nums">{day(review.guest_reviewed_on)}</span>
          ) : (
            <>
              <span className="text-textMuted">Not seen yet</span>
              {airbnb && (
                <span className="block text-xs text-textMuted">
                  Airbnb shows you their review once you have both reviewed, or when the window
                  closes.
                </span>
              )}
            </>
          )}
        </Row>

        <Row
          label="You reviewed"
          testId="review-host"
          tick={!tracked ? 'unknown' : review.host_reviewed_on ? 'done' : 'open'}
        >
          {!tracked ? (
            <span className="text-textMuted">Not tracked</span>
          ) : review.host_reviewed_on ? (
            <>
              <span className="tabular-nums">{day(review.host_reviewed_on)}</span>
              {hostNote && <span className="text-xs text-textMuted"> ({hostNote})</span>}
            </>
          ) : (
            <span className="text-textMuted">Not yet</span>
          )}
        </Row>

        {tracked && review.status && (
          <Row label="Review chase" testId="review-chase">
            {chaseStatusLabel(review.status)}
            {skipped && (
              <span className="block text-xs text-textMuted" data-testid="review-skip-reason">
                {review.skip_reason ? review.skip_reason : 'No reason given'}
              </span>
            )}
          </Row>
        )}

        {tracked && (
          <>
            <Row label="Reminders sent" testId="review-reminders">
              {review.reminder_sends.length > 0 ? (
                <span className="tabular-nums">
                  {formatInstantDays(review.reminder_sends, timeZone)}
                </span>
              ) : (
                <span className="text-textMuted">None</span>
              )}
            </Row>
            {review.reminder_clicks.length > 0 && (
              <Row label="Reminder link opened" testId="review-reminder-clicks">
                <span className="tabular-nums">
                  {formatInstantDays(review.reminder_clicks, timeZone)}
                </span>
                <span className="block text-xs text-textMuted">
                  Opening the link sends nothing.
                </span>
              </Row>
            )}
            <Row label="Thank-you sent" testId="review-thank-you">
              {review.thank_you_sent_at ? (
                <span className="tabular-nums">
                  {formatInstantDays([review.thank_you_sent_at], timeZone)}
                </span>
              ) : (
                <span className="text-textMuted">Not sent</span>
              )}
            </Row>
          </>
        )}
      </dl>

      {!tracked && (
        <p className="text-xs text-textMuted" data-testid="review-untracked">
          No review chase is on record for this stay, so your review, the reminders and the
          thank-you are not tracked here.
        </p>
      )}

      <div className="space-y-1.5">
        <Button variant="secondary" size="sm" asChild>
          <Link to={reviewRowHref(review.reservation_id)} data-testid="review-drafts-link">
            Open review and thank-you drafts
          </Link>
        </Button>
        <p className="text-xs text-textMuted">
          The drafts are on the Reviews page, for stays still being chased. You read and send them
          there.
        </p>
      </div>
    </div>
  )
}
